import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Shield, Check } from 'lucide-react';

export const AccessLoading: React.FC = () => {
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);
  
  // Simulate progressive loading for better perceived performance
  useEffect(() => {
    const timers = [
      setTimeout(() => setCompletedSteps([0]), 400),
      setTimeout(() => setCompletedSteps([0, 1]), 800),
      setTimeout(() => setCompletedSteps([0, 1, 2]), 1200),
    ];
    return () => timers.forEach(clearTimeout);
  }, []);

  const steps = [
    'Validating access token',
    'Verifying account status',
    'Loading your profile'
  ];

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      {/* Background decoration */}
      <div className="fixed inset-0 -z-10">
        <div className="absolute top-1/2 left-1/2 w-[400px] h-[400px] bg-primary/5 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" />
      </div>
      
      <div className="w-full max-w-md">
        <Card className="border-border/50 shadow-lg">
          <CardContent className="p-8 text-center">
            {/* Animated icon */}
            <div className="flex justify-center mb-6">
              <div className="relative">
                <div className="absolute inset-0 bg-primary/20 rounded-full animate-ping" />
                <div className="relative bg-primary/10 p-5 rounded-full">
                  <Shield className="w-10 h-10 text-primary" />
                </div>
              </div>
            </div>
            
            <h2 className="text-xl font-semibold text-foreground mb-2">
              Verifying Access
            </h2>
            <p className="text-sm text-muted-foreground mb-6">
              Preparing your personalized experience
            </p>
            
            {/* Progress steps */}
            <div className="space-y-3 text-left max-w-xs mx-auto">
              {steps.map((step, index) => (
                <div 
                  key={index}
                  className={`flex items-center gap-3 transition-all duration-300 ${
                    completedSteps.includes(index) ? 'opacity-100' : 'opacity-50'
                  }`}
                >
                  <div className={`w-5 h-5 rounded-full flex items-center justify-center transition-colors duration-300 ${
                    completedSteps.includes(index) 
                      ? 'bg-primary text-primary-foreground' 
                      : 'bg-muted'
                  }`}>
                    {completedSteps.includes(index) ? (
                      <Check className="w-3 h-3" />
                    ) : (
                      <div className="w-2 h-2 bg-muted-foreground/30 rounded-full animate-pulse" />
                    )}
                  </div>
                  <span className="text-sm text-muted-foreground">{step}</span>
                </div>
              ))}
            </div>

            {/* Progress bar */}
            <div className="mt-6 pt-4">
              <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                <div 
                  className="h-full bg-primary rounded-full transition-all duration-500 ease-out"
                  style={{ width: `${((completedSteps.length) / steps.length) * 100}%` }}
                />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};